import { Page } from '@adapters/navigation/page';
import type { TaggingMetricMapper } from '@adapters/tagging/taggingMetricMapper';
import { Inject, Injectable } from '@angular/core';
import type { Logger } from '@core/model/domain/logger';
import type { TaggingInitData, TaggingTracker } from '@core/model/domain/taggingTracker';
import {
  loggerToken,
  taggingMetricMapperToken,
  taggingTrackerToken,
} from 'src/conf/di/angularProviderMapper';
import { Buffer } from 'buffer';

type TrackPage = {
  taggingPage: Page;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  taggingPayload?: any;
};

type TrackClick = {
  click: string;
  taggingPage: Page;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  taggingPayload?: any;
};

type TrackPageError = {
  taggingPage: Page;
  errorPageOrigin: string;
  userId: string;
};

type TrackBankSearch = {
  searchTerm: string;
  resultCount: number;
  userId: string;
};

@Injectable({
  providedIn: 'root',
})
export class TrackerService {
  private isTrackingInitialized = false;

  constructor(
    @Inject(taggingTrackerToken) private taggingTracker: TaggingTracker,
    @Inject(taggingMetricMapperToken) private taggingMetricMapper: TaggingMetricMapper,
    @Inject(loggerToken) private loggerService: Logger
  ) {}

  async initializeTaggingTracker(taggingInitDataInBase64: string): Promise<void> {
    const bufferAsString = Buffer.from(taggingInitDataInBase64, 'base64').toString();
    const taggingInitDataDecoded = JSON.parse(bufferAsString) as TaggingInitData;

    this.isTrackingInitialized = await this.taggingTracker.ensureInitialization(taggingInitDataDecoded);

    if (this.isTrackingInitialized) {
      this.taggingMetricMapper.ensureInitialization({
        prodName: taggingInitDataDecoded.prod_name,
        userStatus: taggingInitDataDecoded.user_status,
      });
    }
  }

  trackPage(trackPage: TrackPage): void {
    this.loggerService.logPageView(trackPage.taggingPage);
    this.tagPage(trackPage);
  }

  trackClick(trackClick: TrackClick): void {
    this.loggerService.logEvent(`${trackClick.taggingPage} - ${trackClick.click}`);
    this.tagClick(trackClick);
  }

  trackPageError(trackError: TrackPageError): void {
    this.loggerService.logPageView(trackError.taggingPage);
    this.tagPageError(trackError);
  }

  // Only uses loggerService (not taggingTracker) because TaggingTracker only supports page/click/error events — there is no search event type.
  trackBankSearch(input: TrackBankSearch): void {
    const safeTerm = this.sanitizeSearchTerm(input.searchTerm);
    if (safeTerm == null) {
      return;
    }

    this.loggerService.logEvent('BANK_CHOICE - search', {
      searchTerm: safeTerm,
      resultCount: input.resultCount,
      hasResult: input.resultCount > 0,
      userId: input.userId,
    });
  }

  private sanitizeSearchTerm(rawTerm: string): string | null {
    const term = rawTerm.trim().toLowerCase();
    if (term.length < 2 || term.length > 50) {
      return null;
    }
    if (/@|\d{4,}/.test(term)) {
      return null;
    }
    return term;
  }

  private tagPageError(trackError: TrackPageError): void {
    if (!this.isTrackingInitialized) {
      return;
    }

    const { taggingPage, errorPageOrigin } = trackError;

    const pageMetric = this.taggingMetricMapper.pageMetricMapper(taggingPage);
    const errorMetric = this.taggingMetricMapper.errorMetricMapper(errorPageOrigin);
    this.taggingTracker.captureErrorEvent(errorMetric, pageMetric);
  }

  private tagPage(trackPage: TrackPage): void {
    if (!this.isTrackingInitialized) {
      return;
    }

    const { taggingPage, taggingPayload } = trackPage;

    const pageMetric = this.taggingMetricMapper.pageMetricMapper(taggingPage, taggingPayload);
    this.taggingTracker.capturePageEvent(pageMetric);
  }

  private tagClick(trackClick: TrackClick): void {
    if (!this.isTrackingInitialized) {
      return;
    }

    const { taggingPage, taggingPayload, click } = trackClick;
    const pageMetric = this.taggingMetricMapper.pageMetricMapper(taggingPage, taggingPayload);
    const adaptedClick = this.adaptClickToTaggingSpecification(click);

    this.taggingTracker.captureClickEvent({ click: adaptedClick }, pageMetric);
  }

  private adaptClickToTaggingSpecification(click: string) {
    const clickAdapted = click
      .trim()
      .toLowerCase()
      .replace(/[ -]/g, '_')
      .replace(/['’]/g, '_')
      .replace(/[“”,.]/g, '')
      .replace(/à/g, 'a')
      .replace(/é/g, 'e')
      .replace(/è/g, 'e')
      .replace(/ù/g, 'u')
      .replace(/ç/g, 'c');

    if (!/^[a-z_]+$/.test(clickAdapted)) {
      this.loggerService.logTrace(
        `Tagging error: click should only contain lowercase letters and underscores: ${clickAdapted}`
      );
    }

    return clickAdapted;
  }
}
